import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { selectPaneEditEnabled, setPaneEditEnabled, updateLicense } from "@devices/store";
import { License } from "@devices/types";
import { Box, Chip, Theme, useTheme } from "@mui/material";
import { TextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { EditPrimaryButtons } from "@sidePanel/shared/EditActionButtons";
import { PropertyKey, PropertyRow, PropertyValue } from "@sidePanel/shared/Property";

import { GroupPropertyRow } from "./GroupPropertyRow";

type FormValues = {
    comment: string | null;
};

const useStyles = makeStyles()((mutheme: Theme) => ({
    editBox: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        marginTop: mutheme.spacing(1),
        marginBottom: mutheme.spacing(1),
    },
}));

const LicenseOverviewTab = (props: License) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const editEnabled = useSelector(selectPaneEditEnabled);
    const theme = useTheme();
    const { t } = useTranslation(["common", "sidepanels"]);
    const isUniversal = Boolean(props.universalGroupName);

    const onSubmit = async (payload: FormValues) => {
        try {
            await dispatch(updateLicense({ id: props.id, ...payload }));
            showSuccessToast(t("sidepanels:devicePanel.toast.updateSuccess"));
        } catch (error: unknown) {
            if (error instanceof Error) {
                showErrorToast(error.message);
            }
        }
        dispatch(setPaneEditEnabled(false));
    };

    return (
        <PaneElement.Container>
            <PaneElement.Header>{t("sidepanels:devicePanel.overviewTab.general")}</PaneElement.Header>
            <PropertyRow>
                <PropertyKey>{t("common:label.activationCode")}</PropertyKey>
                <PropertyValue>
                    <PaneElement.CopyableValue value={props.activationCode} />
                </PropertyValue>
            </PropertyRow>
            <PropertyRow style={{ alignItems: "center", marginBottom: theme.spacing(1) }}>
                <PropertyKey>{t("sidepanels:devicePanel.overviewTab.universalStatus")}</PropertyKey>
                <PropertyValue>
                    <Chip
                        label={
                            isUniversal
                                ? t("sidepanels:devicePanel.overviewTab.universal")
                                : t("sidepanels:devicePanel.overviewTab.notUniversal")
                        }
                        color="primary"
                        size="small"
                    />
                </PropertyValue>
            </PropertyRow>
            <Form<FormValues>
                onSubmit={onSubmit}
                initialValues={{ comment: props.comment }}
                render={({ handleSubmit, submitting }) => (
                    <form onSubmit={handleSubmit}>
                        {editEnabled ? (
                            <Box className={classes.editBox}>
                                <PropertyRow style={{ alignItems: "center", marginBottom: theme.spacing(1) }}>
                                    <PropertyKey>{t("common:label.comment")}</PropertyKey>
                                    <PropertyValue>
                                        <TextField name="comment" style={{ marginTop: theme.spacing(1) }} disabled={submitting} />
                                    </PropertyValue>
                                </PropertyRow>
                            </Box>
                        ) : null}
                        <PropertyRow style={{ alignItems: "center", marginBottom: 1 }}>
                            <PropertyKey>{t("sidepanels:devicePanel.overviewTab.deviceSlotStatus")}</PropertyKey>
                            <PropertyValue style={{ marginTop: editEnabled ? theme.spacing(1) : 0 }}>
                                <Chip
                                    label={t("common:status.unused")}
                                    color="primary"
                                    size="small"
                                    clickable={false}
                                    onClick={() => {}}
                                />
                            </PropertyValue>
                        </PropertyRow>
                        <GroupPropertyRow id={props.id} groupId={props.groupId} groupName={props.groupName} />
                        <EditPrimaryButtons loading={submitting} />
                    </form>
                )}
            />
        </PaneElement.Container>
    );
};
export default LicenseOverviewTab;
